/**
 * PolicyPreview - Policy Visualization Component
 *
 * Displays a read-only preview of a policy document showing which
 * resources are affected and which principals have access.
 */

import { useMemo } from 'react';
import { Card, Table, Badge, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { PolicyDocument, Entity } from '@/types/policy';

interface PolicyPreviewProps {
  policy: PolicyDocument | null;
  studies?: Entity[];
  clinics?: Entity[];
  devices?: Entity[];
  dataTypes?: Entity[];
}

export default function PolicyPreview({
  policy,
  studies = [],
  clinics = [],
  devices = [],
  dataTypes = [],
}: PolicyPreviewProps) {
  const { t } = useTranslation();

  // Entity lookups
  const studyLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    studies.forEach((s) => {
      map.set(s.id.toString(), s);
      if (s.study_id) map.set(s.study_id, s);
    });
    return map;
  }, [studies]);

  const clinicLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    clinics.forEach((c) => {
      map.set(c.id.toString(), c);
      if (c.clinic_id) map.set(c.clinic_id, c);
    });
    return map;
  }, [clinics]);

  const deviceLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    devices.forEach((d) => {
      map.set(d.id.toString(), d);
      if (d.device_id) map.set(d.device_id, d);
    });
    return map;
  }, [devices]);

  const dataTypeLookup = useMemo(() => {
    const map = new Map<string, Entity>();
    dataTypes.forEach((dt) => {
      map.set(dt.id.toString(), dt);
      if (dt.data_type_id) map.set(dt.data_type_id, dt);
    });
    return map;
  }, [dataTypes]);

  // Get entity name
  const getEntityName = (id: string, lookup: Map<string, Entity>): string => {
    const entity = lookup.get(id);
    return (
      entity?.name ||
      entity?.study_id ||
      entity?.clinic_id ||
      entity?.device_id ||
      entity?.data_type_id ||
      id
    );
  };

  // Extract principals from policy
  const principals = useMemo(() => {
    if (!policy) return { users: [], userGroups: [], roles: [] };

    const users = new Set<string>();
    const userGroups = new Set<string>();
    const roles = new Set<string>();

    policy.Statement.forEach((stmt) => {
      stmt.Principal?.Users?.forEach((u) => users.add(u));
      stmt.Principal?.UserGroups?.forEach((g) => userGroups.add(g));
      stmt.Principal?.Roles?.forEach((r) => roles.add(r));
    });

    return {
      users: Array.from(users),
      userGroups: Array.from(userGroups),
      roles: Array.from(roles),
    };
  }, [policy]);

  // Extract resources from policy
  const resources = useMemo(() => {
    if (!policy) {
      return {
        studies: [],
        clinics: [],
        devices: [],
        dataTypes: [],
      };
    }

    const studyIds = new Set<string>();
    const clinicIds = new Set<string>();
    const deviceIds = new Set<string>();
    const dataTypeIds = new Set<string>();

    policy.Statement.forEach((stmt) => {
      stmt.Resource.Studies?.forEach((s) => {
        if (s.study_id) studyIds.add(s.study_id);
      });
      stmt.Resource.Clinics?.forEach((c) => {
        if (c.clinic_id) clinicIds.add(c.clinic_id);
      });
      stmt.Resource.Devices?.forEach((d) => {
        if (d.device_id) deviceIds.add(d.device_id);
      });
      stmt.Resource.DataTypes?.forEach((dt) => {
        if (dt.data_type_id) dataTypeIds.add(dt.data_type_id);
      });
    });

    return {
      studies: Array.from(studyIds),
      clinics: Array.from(clinicIds),
      devices: Array.from(deviceIds),
      dataTypes: Array.from(dataTypeIds),
    };
  }, [policy]);

  // Check if policy has Allow and Deny statements
  const hasAllowStatements = policy?.Statement.some((s) => s.Effect === 'Allow') || false;
  const hasDenyStatements = policy?.Statement.some((s) => s.Effect === 'Deny') || false;
  const hasMixedStatements = hasAllowStatements && hasDenyStatements;

  // Check if public (no principals)
  const isPublic = principals.users.length === 0 &&
                   principals.userGroups.length === 0 &&
                   principals.roles.length === 0;

  const totalResources =
    resources.studies.length +
    resources.clinics.length +
    resources.devices.length +
    resources.dataTypes.length;

  if (!policy) {
    return (
      <Alert variant="info">
        {t('admin.policyManagement.noPolicy', 'No policy to preview')}
      </Alert>
    );
  }

  if (policy.Statement.length === 0) {
    return (
      <Alert variant="warning">
        {t('admin.policyManagement.noPolicyStatements', 'Policy has no statements')}
      </Alert>
    );
  }

  return (
    <div className="policy-preview">
      {/* Summary */}
      <Card className="mb-3">
        <Card.Header>
          <strong>{t('admin.policyManagement.policySummary', 'Policy Summary')}</strong>
        </Card.Header>
        <Card.Body>
          <div className="mb-2">
            <strong>{t('admin.policyManagement.version', 'Version')}:</strong>{' '}
            <code>{policy.Version}</code>
          </div>
          <div className="mb-2">
            <strong>{t('admin.policyManagement.statements', 'Statements')}:</strong>{' '}
            {policy.Statement.length}
            {hasAllowStatements && (
              <Badge bg="success" className="ms-2">
                {policy.Statement.filter((s) => s.Effect === 'Allow').length} Allow
              </Badge>
            )}
            {hasDenyStatements && (
              <Badge bg="danger" className="ms-2">
                {policy.Statement.filter((s) => s.Effect === 'Deny').length} Deny
              </Badge>
            )}
          </div>
          {hasMixedStatements && (
            <Alert variant="warning" className="mb-0 mt-3">
              <small>
                ⚠️{' '}
                {t(
                  'admin.policyManagement.mixedPolicy',
                  'This policy contains both Allow and Deny statements. Deny statements always take precedence.'
                )}
              </small>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* Principals */}
      <Card className="mb-3">
        <Card.Header>
          <strong>{t('admin.policyManagement.principals', 'Principals Affected')}</strong>
        </Card.Header>
        <Card.Body>
          {isPublic ? (
            <Alert variant="info" className="mb-0">
              <strong>{t('admin.policyManagement.publicAccess', 'Public Access')}</strong>
              <p className="mb-0 mt-2 small">
                {t(
                  'admin.policyManagement.publicAccessHelp',
                  'This policy applies to all users (no specific principals defined).'
                )}
              </p>
            </Alert>
          ) : (
            <div>
              {principals.users.length > 0 && (
                <div className="mb-2">
                  <strong>{t('admin.policyManagement.users', 'Users')}:</strong>
                  <div className="mt-1">
                    {principals.users.map((user, idx) => (
                      <Badge key={idx} bg="primary" className="me-2 mb-1">
                        {user}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {principals.userGroups.length > 0 && (
                <div className="mb-2">
                  <strong>{t('admin.policyManagement.userGroups', 'User Groups')}:</strong>
                  <div className="mt-1">
                    {principals.userGroups.map((group, idx) => (
                      <Badge key={idx} bg="info" className="me-2 mb-1">
                        {group}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {principals.roles.length > 0 && (
                <div className="mb-2">
                  <strong>{t('admin.policyManagement.roles', 'Roles')}:</strong>
                  <div className="mt-1">
                    {principals.roles.map((role, idx) => (
                      <Badge key={idx} bg="success" className="me-2 mb-1">
                        {role}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Resources */}
      <Card>
        <Card.Header>
          <strong>
            {t('admin.policyManagement.resourcesCovered', 'Resources Covered')} ({totalResources})
          </strong>
        </Card.Header>
        <Card.Body>
          {totalResources === 0 ? (
            <Alert variant="warning" className="mb-0">
              {t(
                'admin.policyManagement.noResources',
                'No resources found in this policy. This is likely an error.'
              )}
            </Alert>
          ) : (
            <div>
              {resources.studies.length > 0 && (
                <div className="mb-3">
                  <strong>{t('admin.policyManagement.studies', 'Studies')}:</strong>
                  <Table striped bordered hover size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('admin.policyManagement.studyId', 'Study ID')}</th>
                        <th>{t('admin.policyManagement.name', 'Name')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.studies.slice(0, 10).map((studyId, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <code>{studyId}</code>
                          </td>
                          <td>{getEntityName(studyId, studyLookup)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {resources.studies.length > 10 && (
                    <small className="text-muted">
                      ... and {resources.studies.length - 10} more
                    </small>
                  )}
                </div>
              )}

              {resources.clinics.length > 0 && (
                <div className="mb-3">
                  <strong>{t('admin.policyManagement.clinics', 'Clinics')}:</strong>
                  <Table striped bordered hover size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('admin.policyManagement.clinicId', 'Clinic ID')}</th>
                        <th>{t('admin.policyManagement.name', 'Name')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.clinics.slice(0, 10).map((clinicId, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <code>{clinicId}</code>
                          </td>
                          <td>{getEntityName(clinicId, clinicLookup)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {resources.clinics.length > 10 && (
                    <small className="text-muted">
                      ... and {resources.clinics.length - 10} more
                    </small>
                  )}
                </div>
              )}

              {resources.devices.length > 0 && (
                <div className="mb-3">
                  <strong>{t('admin.policyManagement.devices', 'Devices')}:</strong>
                  <Table striped bordered hover size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('admin.policyManagement.deviceId', 'Device ID')}</th>
                        <th>{t('admin.policyManagement.name', 'Name')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.devices.slice(0, 10).map((deviceId, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <code>{deviceId}</code>
                          </td>
                          <td>{getEntityName(deviceId, deviceLookup)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {resources.devices.length > 10 && (
                    <small className="text-muted">
                      ... and {resources.devices.length - 10} more
                    </small>
                  )}
                </div>
              )}

              {resources.dataTypes.length > 0 && (
                <div className="mb-3">
                  <strong>{t('admin.policyManagement.dataTypes', 'Data Types')}:</strong>
                  <Table striped bordered hover size="sm" className="mt-2">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('admin.policyManagement.dataTypeId', 'Data Type ID')}</th>
                        <th>{t('admin.policyManagement.name', 'Name')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resources.dataTypes.slice(0, 10).map((dataTypeId, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <code>{dataTypeId}</code>
                          </td>
                          <td>{getEntityName(dataTypeId, dataTypeLookup)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  {resources.dataTypes.length > 10 && (
                    <small className="text-muted">
                      ... and {resources.dataTypes.length - 10} more
                    </small>
                  )}
                </div>
              )}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
